import { Auth0Client } from "@auth0/auth0-spa-js";
import { useEffect, useRef } from "react";
import { useAuth } from "zudoku/hooks";
import { AUTH_SCOPES } from "../../config/authScopes";

const AUTH0_CONFIG = {
  domain: import.meta.env.ZUDOKU_PUBLIC_AUTH0_DOMAIN || "login.ogintegration.us",
  clientId: import.meta.env.ZUDOKU_PUBLIC_AUTH0_CLIENT_ID || "",
  audience: import.meta.env.ZUDOKU_PUBLIC_AUTH0_AUDIENCE || "opengov",
};

const OPENID_CALLBACK_PATH = "/oauth/callback";

// Auth0 silent-auth failure codes that are expected when the browser blocks
// third-party cookies (Firefox TCP, Safari ITP) or when there is simply no SSO
// session. These are not errors; we just cannot pre-populate the session.
const BENIGN_SILENT_AUTH_CODES = new Set(["login_required", "consent_required"]);

function getOAuthCallbackUrl(): string {
  return `${window.location.origin}${OPENID_CALLBACK_PATH}`;
}

let auth0Client: Auth0Client | null = null;

const getAuth0Client = (): Auth0Client => {
  if (!auth0Client) {
    auth0Client = new Auth0Client({
      domain: AUTH0_CONFIG.domain,
      clientId: AUTH0_CONFIG.clientId,
      authorizationParams: {
        audience: AUTH0_CONFIG.audience,
        redirect_uri: getOAuthCallbackUrl(),
        scope: AUTH_SCOPES.join(" "),
      },
    });
  }
  return auth0Client;
};

/**
 * Silent-auth bridge: pre-populates Zudoku's auth state from an existing Auth0
 * SSO session (if any). Strictly additive — never logs the user out. Runs only
 * when Zudoku says the user is not already authenticated, because if they are,
 * Zudoku's native refresh-token flow owns token renewal.
 */
export function AuthOverride() {
  const { isAuthenticated, isPending, setLoggedIn } = useAuth();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    if (isPending) return;
    if (isAuthenticated) return;
    hasRun.current = true;

    const checkAuth0Session = async () => {
      try {
        const client = getAuth0Client();
        const accessToken = await client.getTokenSilently();
        const user = await client.getUser();
        if (user && user.sub && user.email && user.name && accessToken) {
          const orgId = user["org_id"];
          setLoggedIn({
            profile: {
              sub: user.sub,
              email: user.email,
              name: user.name,
              emailVerified: user.email_verified ?? false,
              pictureUrl: user.picture,
              ...(orgId && typeof orgId === "string" ? { org_id: orgId } : {}),
            },
            providerData: {
              type: "openid",
              accessToken,
              expiresOn: new Date(Date.now() + 3600 * 1000),
              tokenType: "Bearer",
            },
          });
        }
      } catch (error: unknown) {
        const code = (error as { error?: string } | undefined)?.error;
        if (code && BENIGN_SILENT_AUTH_CODES.has(code)) {
          console.warn("Auth0 silent auth skipped:", code);
          return;
        }
        console.error("Auth0 silent auth failed:", error);
      }
    };
    void checkAuth0Session();
  }, [isAuthenticated, isPending, setLoggedIn]);

  return null;
}
